import { LoaderCircle, type LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-fg shadow-[0_1px_2px_rgb(0_0_0/0.08),0_4px_12px_-2px_color-mix(in_oklab,var(--primary)_45%,transparent)] hover:brightness-110',
  secondary: 'bg-surface text-fg border border-line shadow-[0_1px_2px_rgb(0_0_0/0.04)] hover:bg-elevated',
  ghost: 'text-muted hover:bg-elevated hover:text-fg',
  subtle: 'bg-elevated text-fg hover:brightness-95 dark:hover:brightness-125',
  danger: 'bg-danger text-white shadow-[0_4px_12px_-2px_color-mix(in_oklab,var(--danger)_45%,transparent)] hover:brightness-110',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-3 text-[13px] rounded-[10px]',
  md: 'h-10 gap-2 px-4 text-sm rounded-xl',
  lg: 'h-12 gap-2 px-6 text-[15px] rounded-2xl',
};

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
  iconAfter?: LucideIcon;
  loading?: boolean;
  children?: ReactNode;
};

export function Button({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconAfter: IconAfter,
  loading = false,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  const inactive = disabled || loading;
  const glyph = size === 'sm' ? 14 : 16;

  return (
    <button
      type="button"
      disabled={inactive}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center font-semibold transition active:scale-[0.98]',
        'disabled:pointer-events-none disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...rest}
    >
      {loading ? (
        <LoaderCircle size={glyph} className="animate-spin" />
      ) : (
        Icon && <Icon size={glyph} strokeWidth={2.2} />
      )}
      {children}
      {IconAfter && !loading && <IconAfter size={glyph} strokeWidth={2.2} />}
    </button>
  );
}

/** Square icon-only button. `label` is required — it becomes the a11y name. */
export function IconButton({
  icon: Icon,
  label,
  variant = 'ghost',
  size = 'md',
  className,
  ...rest
}: Omit<ButtonProps, 'icon' | 'children'> & { icon: LucideIcon; label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center transition',
        'disabled:pointer-events-none disabled:opacity-40',
        VARIANTS[variant],
        size === 'sm' ? 'size-8 rounded-[10px]' : 'size-10 rounded-xl',
        'gap-0 px-0',
        className
      )}
      {...rest}
    >
      <Icon size={size === 'sm' ? 15 : 17} strokeWidth={2.1} />
    </button>
  );
}
